import React, { useEffect, useState } from 'react';
import { Link } from 'wouter';

import { EntitlementReport, fetchEntitlementReport } from '../../api/billing';
import { useSession } from '../../session';

/** Uses the server-provided recovery action; unknown actions produce no link. */
function RecoveryLink({ action, checkoutLabel }: { action: string; checkoutLabel: string }) {
  if (action === 'checkout') {
    return <Link to="/billing">{checkoutLabel}</Link>;
  }
  if (action === 'support') {
    return <Link to="/support">Contact support</Link>;
  }
  return null;
}

/**
 * Renders the server's lifecycle and recovery decision without deriving access
 * or plan status. The only client calculation is days remaining for a supplied
 * trial end timestamp.
 */
export function BillingStatusBannerContent({
  entitlement,
  now = Date.now(),
}: {
  entitlement: EntitlementReport | null;
  now?: number;
}) {
  if (!entitlement) return null;

  const subscription = entitlement.subscription;
  if (subscription.lifecycle === 'payment_failed') {
    return (
      <aside className="billing-status-banner" role="alert" data-state="payment_failed">
        <p>Your last payment failed. You still have full access; please update your card.</p>
        <RecoveryLink action={subscription.recovery_action} checkoutLabel="Update your card" />
      </aside>
    );
  }

  if (subscription.lifecycle === 'suspended') {
    return (
      <aside className="billing-status-banner" role="alert" data-state="suspended">
        <p>Your account is read-only; update your payment method to restore full access.</p>
        <RecoveryLink action={subscription.recovery_action} checkoutLabel="Update payment method" />
      </aside>
    );
  }

  if (subscription.lifecycle === 'trialing' && subscription.entitlement_ends_at) {
    const endAt = Date.parse(subscription.entitlement_ends_at);
    if (!Number.isFinite(endAt)) return null;
    const daysRemaining = Math.max(0, Math.ceil((endAt - now) / 86_400_000));
    const dayLabel = daysRemaining === 1 ? 'day' : 'days';
    return (
      <p className="billing-status-trial-notice page-note" role="status" data-state="trialing">
        Trial ends in {daysRemaining} {dayLabel}.
      </p>
    );
  }

  return null;
}

export function BillingStatusBanner() {
  const { session } = useSession();
  const orgId = session?.org_id;
  const [entitlement, setEntitlement] = useState<EntitlementReport | null>(null);

  useEffect(() => {
    let active = true;
    setEntitlement(null);
    if (!orgId) return () => { active = false; };

    fetchEntitlementReport()
      .then((report) => {
        if (active) setEntitlement(report);
      })
      .catch(() => {
        // Unavailable billing state never produces a guessed customer banner.
        if (active) setEntitlement(null);
      });

    return () => { active = false; };
  }, [orgId]);

  if (!session) return null;
  return <BillingStatusBannerContent entitlement={entitlement} />;
}

export default BillingStatusBanner;
